import { useNavigate } from "@entry/navigation";
import { useReducer } from "react";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { SelectionOption } from "@components/SelectionOption";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Flow, Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useTrumpSidecar } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  TrumpAccountCheckSelectionAction,
  TrumpAccountCheckSelectionState,
  TrumpAccountCheckSelectionType,
} from "./types";

const initialState: TrumpAccountCheckSelectionState = {
  yes: false,
  no: false,
};

const reducer = (
  state: TrumpAccountCheckSelectionState,
  action: TrumpAccountCheckSelectionAction,
) => {
  return { ...initialState, [action.type]: !state[action.type] };
};

export const TrumpAccountCheckRoute = () => {
  const navigate = useNavigate();

  const [{ yes, no }, dispatch] = useReducer(reducer, initialState);

  const {
    goalProps: { flow },
    setGoalProps,
  } = useGoalFlow();

  const { firstName, isSelf } = useTrumpSidecar();

  // Fall back to the first person copy when no name is available.
  const shouldUseSelfCopy = isSelf || !firstName;

  const disabled = !yes && !no;

  const handleSubmit = () => {
    setGoalProps({ hasTrumpAccount: yes });

    navigate(yes ? Routes.TrumpSidecarIntroduction : Routes.TrumpAccountSetup);
  };

  const props: OnboardingLayoutProps = {
    title: shouldUseSelfCopy
      ? i18n.t(Translations.TrumpAccountCheckRouteTitleSelf)
      : i18n.t(Translations.TrumpAccountCheckRouteTitle, { name: firstName }),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.TrumpAccountCheckRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <SelectionOption
        title={
          shouldUseSelfCopy
            ? i18n.t(Translations.TrumpAccountCheckRouteYesLabelSelf)
            : i18n.t(Translations.TrumpAccountCheckRouteYesLabel)
        }
        value={yes}
        onChange={() => dispatch({ type: TrumpAccountCheckSelectionType.Yes })}
      />
      <Spacing size={SpacingSizes.M} />

      <SelectionOption
        title={
          shouldUseSelfCopy
            ? i18n.t(Translations.TrumpAccountCheckRouteNoLabelSelf)
            : i18n.t(Translations.TrumpAccountCheckRouteNoLabel)
        }
        value={no}
        onChange={() => dispatch({ type: TrumpAccountCheckSelectionType.No })}
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
